import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { AuthService } from '@core/services/auth.service';
import { MessageService } from '@shared/services/message.service';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { NotificationService } from 'src/app/account/services/notification.service';
import { IUserConnectedDto } from 'src/app/login/interfaces/login.interfaces';
import { LoginService } from 'src/app/login/services/login.service';
import { UserService } from 'src/app/users-manager/services/user.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsGuard  {

  constructor(
    public authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public loginService: LoginService,
    public userService: UserService,
    private notificationService: NotificationService,
    private messageService: MessageService,
    private analyticsService: AnalyticsService
  ) { }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this._checkPermissionsWithPing(route);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): boolean {
    return this._checkPermissions(childRoute);
  }

  _checkPermissionsWithPing(route: ActivatedRouteSnapshot): Observable<boolean> {
    this.authService.isPingAlreadyExecuted = true;
    return this.loginService.ping().pipe(
      switchMap(responseBody => { return this._ping(responseBody, route); }),
      catchError(err => { return of(this._checkPermissions(route)); })
    );
  }

  _ping(responseBody: IUserConnectedDto, route: ActivatedRouteSnapshot): Observable<boolean> {
    const username = responseBody.username;
    if (this.authService.userLogged?.username === '') {
      return this.userService.getByUsername(username).pipe(
        map(response => {
          this.authService.authResult = 1;
          this.authService.userLogged = response;
          this.messageService.startTimer()


          this.analyticsService.trackInit({
            idUser: this.authService.userLogged.idUser,
            email: this.authService.userLogged.username,
            firstName: this.authService.userLogged.entityFullName.split(' ')[0],
            lastName: this.authService.userLogged.entityFullName.split(' ').slice(1).join(' ')
          });

          return this._checkPermissions(route);
        }),
        catchError(err => { return of(this._checkPermissions(route)); })
      );
    } else if(this.authService.isAuthenticated()) {
      this.notificationService.getUnreadNotificationsCount().subscribe((count) => {
        this.notificationService.unreadNotificationsCount = count;
      });

      this.analyticsService.trackInit({
        idUser: this.authService.userLogged.idUser,
        email: this.authService.userLogged.username,
        firstName: this.authService.userLogged.entityFullName.split(' ')[0],
        lastName: this.authService.userLogged.entityFullName.split(' ').slice(1).join(' ')
      });
      return of(this._checkPermissions(route));
    } else {
      return of(this._checkPermissions(route));
    }
  }

  _checkPermissions(route: ActivatedRouteSnapshot): boolean {
    if (
      !this.authService.isAuthenticated() ||
      (route.data.expectedIdProfile.indexOf(this.authService.userLogged?.idProfile) < 0)
    ) {
      this.router.navigate(['/home'], { relativeTo: this.activatedRoute });
      return false;
    }
    return true;
  }
}
